import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  Phone,
  Email,
  LocationOn,
  AccessTime,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGetPages } from '../../hooks/storefront/useGetPages';
import logo from '../../assets/logo/logo.png';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lng = i18n.language || "en";

  const { pages } = useGetPages();

  // Mock data - will be replaced with actual data
  const companyInfo = {
    name: t('app.name'),
    description: t('app.description'),
    phone: '+966 50 123 4567',
    email: 'info@kwdstore.com',
    address: 'Riyadh, Saudi Arabia',
    workingHours: 'Sat - Thu: 9:00 AM - 10:00 PM',
  };

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: '#' },
    { name: 'Twitter', icon: Twitter, url: '#' },
    { name: 'Instagram', icon: Instagram, url: '#' },
    { name: 'LinkedIn', icon: LinkedIn, url: '#' },
    { name: 'YouTube', icon: YouTube, url: '#' },
  ];

  const staticPages = [
    { name: t('footer.contactUs'), slug: '/contact' },
  ];

  const dynamicPages = pages.map(page => ({
    name: page.title?.[lng] || page.title?.en || page.title?.ar || "",
    slug: `/page/${page.slug}`
  }));

  const allPages = [...staticPages, ...dynamicPages];

  return (
    <Box
      component="footer"
      sx={{
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #0F0F0F 100%)'
          : 'linear-gradient(135deg, #2D2D2D 0%, #1A1A1A 100%)',
        color: 'white',
        py: 6,
        mt: 'auto',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 -4px 20px rgba(0,0,0,0.3)'
          : '0 -4px 20px rgba(0,0,0,0.1)',
        borderTop: theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.05)'
          : '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={1}>
          {/* Column 1: Logo + Social Links */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src={logo}
                  alt={companyInfo.name}
                  style={{
                    height: 35,
                    marginRight: 12,
                    filter: 'drop-shadow(0px 2px 4px rgba(255,215,0,0.3))'
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6, color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0' }}>
                {companyInfo.description}
              </Typography>

              {/* Social Links */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <IconButton
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.05)',
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,215,0,0.15)' : 'rgba(255,215,0,0.1)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Icon />
                    </IconButton>
                  );
                })}
              </Box>
            </Box>
          </Grid>

          {/* Column 2: Pages & Policies */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: theme.palette.primary.main }}>
              {t('footer.quickLinks')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {allPages?.map((page) => (
                <Link
                  key={page.slug}
                  href={page.slug}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  {page.name}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 3: Contact Info */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: theme.palette.primary.main }}>
              {t('footer.contactInfo')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Phone */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Phone sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>
                    {t('footer.phone')}
                  </Typography>
                  <Link
                    href={`tel:${companyInfo.phone}`}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    {companyInfo.phone}
                  </Link>
                </Box>
              </Box>

              {/* Email */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Email sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>
                    {t('footer.email')}
                  </Typography>
                  <Link
                    href={`mailto:${companyInfo.email}`}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    {companyInfo.email}
                  </Link>
                </Box>
              </Box>

              {/* Address */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>
                    {t('footer.address')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0' }}>
                    {companyInfo.address}
                  </Typography>
                </Box>
              </Box>

              {/* Working Hours */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>
                    {t('footer.workingHours')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0' }}>
                    {companyInfo.workingHours}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box sx={{ mt: 6, pt: 4, borderTop: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(255,255,255,0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0' }}>
              © {new Date().getFullYear()} {companyInfo.name}. {t('footer.allRightsReserved')}.
            </Typography>
            {/* <Box sx={{ display: 'flex', gap: 2 }}>
              <Link
                href="/pages/privacy-policy"
                sx={{
                  color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {t('footer.privacy')}
              </Link>
              <Link
                href="/pages/terms-conditions"
                sx={{
                  color: theme.palette.mode === 'dark' ? '#B0B0B0' : '#B0B0B0',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {t('footer.terms')}
              </Link>
            </Box> */}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
